const RYTHMES = {
    maintenant: { label: 'Maintenant', heures: null },
    h6: { label: 'Toutes les 6 h', heures: 6 },
    h8: { label: 'Toutes les 8 h', heures: 8 },
    h12: { label: 'Toutes les 12 h', heures: 12 },
    h24: { label: 'Toutes les 24 h', heures: 24 },
    h0h12h24: { label: 'H0 · H12 · H24', heures: [0, 12, 24] }
}

export { RYTHMES }

const MINUTES = { minute: 1, heure: 60, jour: 1440 }

export function minutesDeSoin(ligne) {
    if (ligne.rythmeMode === 'unique') return [0]
    const intervalle = Number(ligne.intervalleValeur) * MINUTES[ligne.intervalleUnite]
    const duree = Number(ligne.dureeValeur) * MINUTES[ligne.dureeUnite]
    if (!Number.isInteger(intervalle) || intervalle < 1 || !Number.isInteger(duree) || duree < 1 || duree > 30 * 1440) {
        throw new Error('Indiquez un rythme et une durée valides (30 jours maximum).')
    }
    const prises = Math.ceil(duree / intervalle)
    if (prises > 1000) throw new Error('Ce rythme génère plus de 1 000 prises : augmentez l’intervalle ou fractionnez le plan.')
    return Array.from({ length: prises }, (_, index) => index * intervalle)
}

export function delaiDebutMinutes(ligne) {
    if (ligne.debutMode !== 'differe') return 0
    const delai = Number(ligne.debutValeur) * MINUTES[ligne.debutUnite]
    if (!Number.isInteger(delai) || delai < 1 || delai > 30 * 1440) throw new Error('Indiquez un délai de démarrage valide (30 jours maximum).')
    return delai
}

export function heuresDeSoin(rythme, dureeJours) {
    const option = RYTHMES[rythme]
    const jours = Number(dureeJours)
    if (!option || !Number.isInteger(jours) || jours < 1 || jours > 7) throw new Error('Rythme ou durée invalide.')
    if (option.heures === null) return [0]
    if (Array.isArray(option.heures)) return option.heures.slice()
    const heures = []
    for (let heure = 0; heure < jours * 24; heure += option.heures) heures.push(heure)
    return heures
}

export function quantiteTotale(dose, nombrePrises) {
    const valeur = Number(dose)
    if (!Number.isFinite(valeur) || valeur <= 0 || !Number.isInteger(nombrePrises) || nombrePrises < 1) {
        throw new Error('Dose et nombre de prises invalides.')
    }
    const quantite = Math.round((valeur * nombrePrises + Number.EPSILON) * 100) / 100
    if (quantite > 10000) throw new Error('Quantité totale supérieure à 10 000 : fractionnez le plan.')
    return quantite
}

export function doseCliniqueParPrise(medicament, quantiteUnites, doseSaisie = '') {
    const quantite = Number(quantiteUnites)
    const teneur = Number(medicament && medicament.dosage)
    const uniteBrute = medicament && medicament.unite_dosage &&
        (medicament.unite_dosage.abreviation || medicament.unite_dosage.libelle)
    const unite = String(uniteBrute || '').trim().toLocaleLowerCase()
    const formats = { mg: 'mg', milligramme: 'mg', milligrammes: 'mg', g: 'g', gramme: 'g', grammes: 'g',
        'µg': 'µg', 'μg': 'µg', mcg: 'µg', microgramme: 'µg', microgrammes: 'µg' }
    if (Number.isFinite(quantite) && quantite > 0 && Number.isFinite(teneur) && teneur > 0 && formats[unite]) {
        let total = Math.round((quantite * teneur + Number.EPSILON) * 1000) / 1000
        let uniteTotale = formats[unite]
        if (uniteTotale === 'mg' && total >= 1000) { total /= 1000; uniteTotale = 'g' }
        return `${String(total).replace('.', ',')} ${uniteTotale}`
    }
    const saisie = String(doseSaisie || '').trim()
    if (!/\d/.test(saisie) || !/[a-zA-Zµμ]/.test(saisie) || saisie.length > 120) {
        throw new Error('Précisez la dose clinique et son unité pour ce médicament (ex. 500 mg).')
    }
    return saisie
}

// Recette du 03/10 : un plan non réglé se modifie dans le même formulaire. Convertit le plan
// renvoyé par le serveur (lignes enregistrées) en saisie du formulaire express.
function enUnite(minutes) {
    const valeur = Number(minutes)
    if (valeur > 0 && valeur % 1440 === 0) return { valeur: valeur / 1440, unite: 'jour' }
    if (valeur > 0 && valeur % 60 === 0) return { valeur: valeur / 60, unite: 'heure' }
    return { valeur, unite: 'minute' }
}

export function saisieDepuisPlan(plan, voies = []) {
    const normaliser = valeur => String(valeur || '').trim().toLocaleLowerCase()
    const lignes = (plan.medicaments || []).map(medicament => {
        const repete = Number(medicament.intervalle_minutes) > 0 && Number(medicament.duree_minutes) > 0
        const intervalle = repete ? enUnite(medicament.intervalle_minutes) : null
        const duree = repete ? enUnite(medicament.duree_minutes) : null
        const voie = voies.find(item => normaliser(item.designation) === normaliser(medicament.voie))
        return {
            medicamentId: medicament.hors_catalogue ? '' : (medicament.medicament_id || ''),
            recherche: medicament.libelle || '', horsCatalogue: medicament.hors_catalogue === true,
            voieId: voie ? voie.id : '', dose: Number(medicament.dose) > 0 ? Number(medicament.dose) : 1,
            doseClinique: medicament.dosage || '',
            rythmeMode: repete ? 'repete' : 'unique',
            intervalleValeur: intervalle ? intervalle.valeur : '', intervalleUnite: intervalle ? intervalle.unite : 'heure',
            dureeValeur: duree ? duree.valeur : '', dureeUnite: duree ? duree.unite : 'jour',
            // Le démarrage est recalculé à l'enregistrement de la modification.
            debutMode: medicament.debut_mode === 'imprecis' ? 'imprecis' : 'maintenant', debutValeur: '', debutUnite: 'heure'
        }
    })
    return {
        admissionId: plan.admission_id || '', chambreId: '', observationId: plan.observation_id || '',
        joursChambre: Number(plan.jours_chambre) || 1, joursObservation: Number(plan.jours_observation) || 1,
        lignes, actes: (plan.actes || []).map(acte => ({ acteId: acte.acte_id, nombre: Number(acte.nombre) || 1 })),
        consigne: plan.consigne || ''
    }
}

export function construirePlanExpress({ admission, chambre = '', observation = '', joursChambre = '', joursObservation = '', lignes, actes = [], consigne = '', debut, contexte }) {
    if (!admission || !Number(admission.admission_id)) throw new Error('Choisissez une admission.')
    if (Number(admission.is_with_room) === 1 && (!Number.isInteger(Number(joursChambre)) || Number(joursChambre) < 1 || Number(joursChambre) > 127)) {
        throw new Error('Indiquez une durée probable d’hospitalisation entre 1 et 127 jours.')
    }
    if ((!Array.isArray(lignes) || !lignes.length) && (!Array.isArray(actes) || !actes.length)) throw new Error('Ajoutez au moins un médicament ou un acte de soins.')
    if (!contexte || !Number(contexte.consultation_id) || !Number(contexte.evolution_id) || !Number(contexte.patient_id)) {
        throw new Error('Contexte de consultation incomplet.')
    }

    const produits = []
    const protocole_a_suivre = []
    const medicamentsVus = new Set()
    for (const ligne of lignes) {
        const medicament = ligne.medicament
        const horsCatalogue = ligne.horsCatalogue === true
        const nom = horsCatalogue ? String(ligne.nomLibre || '').trim() : medicament && medicament.libelle
        if (horsCatalogue && nom.length < 2) throw new Error('Saisissez le nom du médicament hors catalogue.')
        if (!horsCatalogue && (!medicament || !Number(medicament.id))) throw new Error('Choisissez un médicament proposé ou confirmez la prescription hors catalogue.')
        if (!horsCatalogue && (!Number.isFinite(Number(medicament.prix)) || Number(medicament.prix) <= 0)) {
            throw new Error('Ce médicament n’a pas de tarif configuré. Faites renseigner son prix avant de créer ce plan express.')
        }
        const cle = horsCatalogue ? 'libre:' + nom.toLocaleLowerCase() : 'catalogue:' + medicament.id
        if (medicamentsVus.has(cle)) throw new Error('Un médicament ne peut figurer qu’une fois dans le même plan.')
        medicamentsVus.add(cle)
        if (!ligne.voie || !ligne.voie.designation) throw new Error('Choisissez une voie d’administration.')
        const ancienFormat = !ligne.rythmeMode
        const dureeEffective = ancienFormat ? (ligne.rythme === 'h0h12h24' ? 2 : (ligne.rythme === 'maintenant' ? 1 : Number(ligne.dureeJours))) : (ligne.rythmeMode === 'unique' ? null : Number(ligne.dureeValeur) * MINUTES[ligne.dureeUnite] / 1440)
        const offsets = ancienFormat ? heuresDeSoin(ligne.rythme, dureeEffective).map(heure => heure * 60) : minutesDeSoin(ligne)
        const quantite = quantiteTotale(ligne.dose, offsets.length)
        const dosage = doseCliniqueParPrise(medicament, ligne.dose, ligne.doseClinique)
        produits.push({
            produit: horsCatalogue ? null : medicament,
            autre_produit: horsCatalogue ? nom : '',
            produit_qte: quantite,
            mode_administration: ligne.voie,
            produit_source: { champ: horsCatalogue ? 'Externe' : 'Interne' },
            is_not_in_list_produit: horsCatalogue,
            intervalle_minutes: ancienFormat ? (typeof RYTHMES[ligne.rythme].heures === 'number' ? RYTHMES[ligne.rythme].heures * 60 : null) : (ligne.rythmeMode === 'unique' ? null : Number(ligne.intervalleValeur) * MINUTES[ligne.intervalleUnite]),
            duree_jours: dureeEffective,
            debut,
            debut_mode: ligne.debutMode || 'maintenant',
            debut_delai_minutes: ancienFormat ? 0 : delaiDebutMinutes(ligne)
        })
        protocole_a_suivre.push({
            medicament: nom,
            medicament_id: horsCatalogue ? null : medicament.id,
            immediat: ancienFormat && ligne.rythme === 'maintenant',
            protocole_hn: offsets.map((minutes, index) => ({
                // Le suivi existant indexe les prises par un entier unique ; la date réelle
                // est calculée avec l'offset en minutes sur l'horloge du serveur.
                hn: ancienFormat ? (ligne.rythme === 'maintenant' ? null : minutes / 60) : index,
                minutes, dosage, immediat: ancienFormat && ligne.rythme === 'maintenant'
            }))
        })
    }

    const actesVus = new Set()
    const actesPlan = actes.map(ligne => {
        const acte = ligne.acte
        if (!acte || !Number(acte.id)) throw new Error('Choisissez un acte du catalogue.')
        if (actesVus.has(Number(acte.id))) throw new Error('Un acte ne peut figurer qu’une fois dans le même plan.')
        actesVus.add(Number(acte.id))
        const nombre = Number(ligne.nombre)
        if (!Number.isInteger(nombre) || nombre < 1 || nombre > 127) throw new Error('Indiquez un nombre d’actes entre 1 et 127.')
        return { acte, acte_nombre_fois: nombre }
    })

    return {
        admission, chambre, chambre_nb_jour: joursChambre, observation, observation_nb_jour: joursObservation,
        produits, protocole_a_suivre, consommables: [], actes: actesPlan,
        cat_consigne: consigne.trim() ? [{ hn: 'Général', consigne: consigne.trim() }] : [],
        ...contexte, protocole_id: 0, model_facturation_acte: ''
    }
}
